import { useEffect, useState } from 'react';
import L from 'leaflet';
import { getMode } from '../lib/profiles';
import { needsWater } from '../lib/coverage';
import { rainGrid } from '../lib/irrigation';
import { sprayShape } from '../lib/spray';

const KEY = 'giss_rain';
export const rainOn = () => localStorage.getItem(KEY) === '1';
export const setRainOn = (v) => { localStorage.setItem(KEY, v ? '1' : '0'); window.dispatchEvent(new Event('giss-rain')); };
export function useRainOn() {
  const [on, setOn] = useState(rainOn);
  useEffect(() => { const h = () => setOn(rainOn()); window.addEventListener('giss-rain', h); return () => window.removeEventListener('giss-rain', h); }, []);
  return on;
}
export const RAIN_COL = { dry: '#dc2626', low: '#f59e0b', high: '#2563eb', run: '#7c3aed' };
export const rainTag = (t, name, r) => `<b>${name.replace(/[<>&]/g, '')}</b><span>Ø ${String(Math.round(r.mean * 10) / 10).replace('.', ',')} mm</span>${['dry', 'low', 'ok', 'high', 'run'].map((k) => `<span class="rn-${k}">${r.pct[k]} % ${t['rn_' + k]}</span>`).join('')}`;

// garden: water applied per watering vs. need (run times from the zone plan) – too little / fitting / too much / run-off
export default function useRainLayer(mapRef, gardens, project, t) {
  const on = useRainOn();
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !on || getMode() !== 'garden' || !project) return undefined;
    const rend = L.canvas({ padding: 0.3 }), g = L.layerGroup();
    (gardens || []).filter((x) => x.pts?.length > 2 && needsWater(x)).forEach((x) => {
      const r = rainGrid(x, project);
      r.cells.forEach((k) => g.addLayer(L.rectangle(k.b, { renderer: rend, stroke: false, fillColor: RAIN_COL[k.k], fillOpacity: 0.42, interactive: false })));
      const east = x.pts.reduce((a, p) => (p[1] > a[1] ? p : a), x.pts[0]);
      g.addLayer(L.marker(east, { interactive: false, keyboard: false, icon: L.divIcon({ className: 'dry-tag-wrap', iconSize: null, html: `<div class="dry-tag out rain-tag" data-testid="rain-tag-${x.id}">${rainTag(t, x.name, r)}</div>` }) }));
    });
    g.addTo(map);
    return () => g.remove();
  }, [mapRef, gardens, project, on, t]);
}

// proposed sprinkler positions (dashed, not yet saved)
export function usePreviewLayer(mapRef) {
  const [heads, setHeads] = useState(null);
  useEffect(() => { const h = (e) => setHeads(e.detail); window.addEventListener('giss-irr-preview', h); return () => window.removeEventListener('giss-irr-preview', h); }, []);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !heads?.length) return undefined;
    const g = L.layerGroup(heads.flatMap((f) => [
      L.polygon(sprayShape(f), { color: '#ea580c', weight: 1.5, dashArray: '5 4', fillColor: '#fb923c', fillOpacity: 0.18, interactive: false, className: 'irr-prev-shape' }),
      L.circleMarker([f.lat, f.lon], { radius: 5, color: '#fff', weight: 2, fillColor: '#ea580c', fillOpacity: 1, interactive: false, className: 'irr-prev-dot' }),
    ])).addTo(map);
    return () => g.remove();
  }, [mapRef, heads]);
}
